import React, { useState } from 'react';
import { Calendar, Users2, Tag, UserCircle, Users, Archive, CheckSquare } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useFollowUps } from '../context/FollowUpsContext';
import { summariesData } from './Summaries';

const userEmail = 'user@example.com'; // In a real app, this would come from auth context

// Extract and flatten follow-ups from summaries
const followUpsData = summariesData.reduce((acc, summary) => {
  const followUps = summary.followUps.map(followUp => ({
    ...followUp,
    summaryId: summary.id,
    summaryTitle: summary.title,
    createdAt: summary.date
  }));
  return [...acc, ...followUps];
}, [] as any[]);

const tagConfig = {
  triage: { label: 'Triage', color: 'bg-blue-500' },
  own: { label: 'Own', color: 'bg-green-500' },
  delegate: { label: 'Delegate', color: 'bg-yellow-500' },
  archive: { label: 'Archive', color: 'bg-red-500' }
};

const FollowUps = () => {
  const [searchParams] = useSearchParams();
  const summaryId = searchParams.get('summary');
  const { followUps, updateFollowUp } = useFollowUps();
  const [animatingCards, setAnimatingCards] = useState<Record<number, boolean>>({});

  const filteredFollowUps = summaryId
    ? followUpsData.filter(followUp => followUp.summaryId === Number(summaryId))
    : followUpsData;

  // Sort in reverse chronological order
  filteredFollowUps.sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return dateA.getTime() - dateB.getTime();
  });

  // Group follow-ups by tag
  const groupedFollowUps = filteredFollowUps.reduce((acc, followUp) => {
    const tag = followUps[followUp.id]?.tag || 'triage';
    if (!acc[tag]) acc[tag] = [];
    acc[tag].push(followUp);
    return acc;
  }, {} as Record<string, typeof filteredFollowUps>);

  const handleCheckboxChange = (followUpId: number, checked: boolean) => {
    if (checked) {
      setAnimatingCards(prev => ({ ...prev, [followUpId]: true }));
      setTimeout(() => {
        setAnimatingCards(prev => ({ ...prev, [followUpId]: false }));
      }, 300);
      updateFollowUp(followUpId, { tag: 'archive', status: 'completed' });
    }
  };

  const handleTagChange = (followUpId: number, newTag: 'own' | 'delegate' | 'archive') => {
    setAnimatingCards(prev => ({ ...prev, [followUpId]: true }));
    setTimeout(() => {
      setAnimatingCards(prev => ({ ...prev, [followUpId]: false }));
      updateFollowUp(followUpId, { tag: newTag });
    }, 300);
  };

  const handleDelegateClick = (followUpId: number) => {
    const followUp = filteredFollowUps.find(f => f.id === followUpId);
    const summary = summariesData.find(s => s.id === followUp?.summaryId);
    
    if (followUp && summary) {
      const subject = encodeURIComponent(followUp.title);
      const body = encodeURIComponent(
        `Hi,\n\nI'd like to delegate this task to you:\n\n` +
        `Task: ${followUp.title}\n` +
        `Description: ${followUp.description}\n\n` +
        `Context from meeting "${summary.title}":\n${summary.summary}\n\n` +
        `Due date: ${followUp.dueDate}\n\n` +
        `Best regards`
      );
      
      window.open(`mailto:?subject=${subject}&body=${body}&from=${userEmail}`);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Follow-Ups</h1>
        <p className="text-gray-600">Track and manage action items from your meetings.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 auto-rows-min">
        {Object.entries(tagConfig).map(([tag, config]) => {
          const items = groupedFollowUps[tag] || [];
          const shouldShow = tag === 'triage' || items.length > 0;
          
          return shouldShow && (
            <section key={tag} className="space-y-4">
              <div className={`flex items-center gap-2 px-4 py-2 ${config.color} text-white rounded-lg`}>
                <Tag className="h-4 w-4" />
                <h2 className="font-semibold">{config.label}</h2>
                <span className="ml-auto bg-white/20 px-2 py-0.5 rounded-full text-sm">
                  {items.length}
                </span>
              </div>
              
              <div className="space-y-4 min-h-[100px]">
                {items.map((followUp) => (
                  <div
                    key={followUp.id}
                    className={`bg-white p-4 rounded-lg border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all ${
                      animatingCards[followUp.id] ? 'follow-up-exit' : 'follow-up-enter'
                    }`}
                  >
                              <div className="flex items-start gap-3">
                                {(tag === 'own' || tag === 'delegate') && (
                                  <input
                                    type="checkbox"
                                    checked={false}
                                    onChange={(e) => handleCheckboxChange(followUp.id, e.target.checked)}
                                    className="mt-1 h-4 w-4 rounded border-2 border-gray-300 text-primary focus:ring-primary"
                                  />
                                )}
                                <div className="flex-1">
                                  <div className="flex items-start justify-between mb-2">
                                  <h3 className="font-semibold text-gray-900 mb-2">{followUp.title}</h3>
                                  {tag === 'delegate' && (
                                    <button
                                      onClick={() => handleDelegateClick(followUp.id)}
                                      className="text-sm font-medium text-yellow-600 hover:text-yellow-700 group"
                                    >
                                      <span className="group-hover:hidden">Ready to delegate</span>
                                      <span className="hidden group-hover:inline">Click to delegate</span>
                                    </button>
                                  )}
                                  {tag === 'own' && (
                                    <span className="text-sm font-medium text-green-600">Owned</span>
                                  )}
                                  </div>
                                  <p className="text-sm text-gray-600">{followUp.description}</p>
                                  
                                  <div className="mt-4 pt-3 border-t border-gray-100">
                                    <div className="flex items-center justify-between text-sm text-gray-600">
                                      <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-1">
                                          <Calendar className="h-4 w-4" />
                                          <span>{followUp.createdAt}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <Users2 className="h-4 w-4" />
                                          <span>{followUp.assignee}</span>
                                        </div>
                                        <a href={`/summaries?id=${followUp.summaryId}`} className="text-primary hover:underline">
                                          {followUp.summaryTitle}
                                        </a>
                                      </div>
                                      {tag === 'triage' && (
                                        <div className="flex items-center gap-1">
                                          <button
                                            onClick={() => handleTagChange(followUp.id, 'own')}
                                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                                            title="Own"
                                          >
                                            <UserCircle className="h-4 w-4" />
                                          </button>
                                          <button
                                            onClick={() => handleTagChange(followUp.id, 'delegate')}
                                            className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded-full transition-colors"
                                            title="Delegate"
                                          >
                                            <Users className="h-4 w-4" />
                                          </button>
                                          <button
                                            onClick={() => handleTagChange(followUp.id, 'archive')}
                                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                            title="Archive"
                                          >
                                            <Archive className="h-4 w-4" />
                                          </button>
                                        </div>
                                      )}
                                      {tag === 'own' && (
                                        <button
                                          onClick={() => handleTagChange(followUp.id, 'delegate')}
                                          className="text-sm text-yellow-600 hover:text-yellow-700"
                                        >
                                          delegate instead?
                                        </button>
                                      )}
                                      {tag === 'delegate' && (
                                        <button
                                          onClick={() => handleTagChange(followUp.id, 'own')}
                                          className="text-sm text-green-600 hover:text-green-700"
                                        >
                                          own instead?
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default FollowUps;